import {
  Avatar,
  Button,
  Card,
  Col,
  Dropdown,
  Layout,
  Menu,
  Row,
  Skeleton,
  Statistic,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import {
  BellOutlined,
  DollarOutlined,
  LogoutOutlined,
  MedicineBoxOutlined,
  SettingOutlined,
  TeamOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import ChangePasswordForm from "../../components/ChangePasswordForm";
import PartnerNotificationBell from "../../components/PartnerNotificationBell";
import {
  getPartnerNotifications,
  getPortalBilling,
  getPortalPatientPrescriptions,
  getPortalPatients,
  getPortalPickupQueue,
} from "../../services/partnerOrgService";

const { Header, Sider, Content } = Layout;
const { Title, Text } = Typography;

const toArray = (d) => Array.isArray(d) ? d : (d?.results ?? []);
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString() : "—";
const fmtCurrency = (v) => v != null ? `₱${parseFloat(v).toFixed(2)}` : "—";

const STATUS_COLOR = { READY: "green", PENDING: "gold", DISPENSED: "default" };

export default function PartnerOrgDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [collapsed,    setCollapsed]    = useState(false);
  const [selectedMenu, setSelectedMenu] = useState("patients");
  const [loading,      setLoading]      = useState(false);

  // patients tab
  const [patients,          setPatients]          = useState([]);
  const [patientsLoaded,    setPatientsLoaded]    = useState(false);
  const [expandedPatient,   setExpandedPatient]   = useState(null);
  const [patientRxMap,      setPatientRxMap]      = useState({});
  const [patientRxLoading,  setPatientRxLoading]  = useState({});

  // queue tab
  const [queue,         setQueue]         = useState([]);
  const [queueLoaded,   setQueueLoaded]   = useState(false);
  const [queueLoading,  setQueueLoading]  = useState(false);

  // billing tab
  const [billingData,     setBillingData]     = useState(null);
  const [billingLoaded,   setBillingLoaded]   = useState(false);
  const [billingLoading,  setBillingLoading]  = useState(false);

  // notifications tab
  const [notifItems,    setNotifItems]    = useState([]);
  const [notifLoaded,   setNotifLoaded]   = useState(false);
  const [notifLoading,  setNotifLoading]  = useState(false);
  const [notifFilter,   setNotifFilter]   = useState("all"); // "all" | "unread"

  const loadPatients = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getPortalPatients();
      setPatients(toArray(res.data).map(r => ({ ...r, key: r.id })));
      setPatientsLoaded(true);
    } catch {
      message.error("Failed to load patient list.");
    } finally { setLoading(false); }
  }, []);

  const loadQueue = useCallback(async () => {
    setQueueLoading(true);
    try {
      const res = await getPortalPickupQueue();
      setQueue(toArray(res.data).map(r => ({ ...r, key: r.id })));
      setQueueLoaded(true);
    } catch {
      message.error("Failed to load pickup queue.");
    } finally { setQueueLoading(false); }
  }, []);

  const loadBilling = useCallback(async () => {
    setBillingLoading(true);
    try {
      const res = await getPortalBilling();
      setBillingData(res.data);
      setBillingLoaded(true);
    } catch {
      message.error("Failed to load billing.");
    } finally { setBillingLoading(false); }
  }, []);

  const loadNotifications = useCallback(async () => {
    setNotifLoading(true);
    try {
      const res = await getPartnerNotifications();
      setNotifItems(toArray(res.data));
      setNotifLoaded(true);
    } catch {
      message.error("Failed to load notifications.");
    } finally { setNotifLoading(false); }
  }, []);

  useEffect(() => {
    if (selectedMenu === "patients"       && !patientsLoaded) loadPatients();
    if (selectedMenu === "queue"          && !queueLoaded)    loadQueue();
    if (selectedMenu === "billing"        && !billingLoaded)  loadBilling();
    if (selectedMenu === "notifications"  && !notifLoaded)    loadNotifications();
  }, [selectedMenu, patientsLoaded, queueLoaded, billingLoaded, notifLoaded,
      loadPatients, loadQueue, loadBilling, loadNotifications]);

  const loadPatientRx = async (pin) => {
    if (patientRxMap[pin] !== undefined) return;
    setPatientRxLoading(p => ({ ...p, [pin]: true }));
    try {
      const res = await getPortalPatientPrescriptions(pin);
      setPatientRxMap(m => ({ ...m, [pin]: toArray(res.data) }));
    } catch {
      setPatientRxMap(m => ({ ...m, [pin]: [] }));
    } finally {
      setPatientRxLoading(p => ({ ...p, [pin]: false }));
    }
  };

  const handleRowExpand = (expanded, record) => {
    if (expanded && record.dhn_pin) {
      setExpandedPatient(record.dhn_pin);
      loadPatientRx(record.dhn_pin);
    } else {
      setExpandedPatient(null);
    }
  };

  const handleLogout = () => { logout(); navigate("/login"); };
  const userMenuItems = [
    { key: "logout", icon: <LogoutOutlined />, label: "Logout", danger: true, onClick: handleLogout },
  ];

  // ── Patient table ──────────────────────────────────────────────────────────
  const patientCols = [
    { title: "Patient Name", dataIndex: "patient_display_name", key: "name", render: n => <Text strong>{n}</Text> },
    { title: "DHN PIN",      dataIndex: "dhn_pin",              key: "pin",
      render: pin => <Text code style={{ fontWeight: 600, color: "#1677ff" }}>{pin ?? "—"}</Text> },
    { title: "Pending Rx",   dataIndex: "pending_prescriptions",key: "pending",
      render: n => n > 0 ? <Tag color="orange">{n} pending</Tag> : <Tag color="default">None</Tag> },
    { title: "Authorised",   dataIndex: "authorised_at",        key: "auth", width: 130, render: fmtDate },
    { title: "Status",       dataIndex: "is_active",            key: "status", width: 90,
      render: v => v ? <Tag color="green">Active</Tag> : <Tag>Inactive</Tag> },
  ];

  const rxExpandCols = [
    { title: "Medication",  dataIndex: "medication",              key: "med",      ellipsis: true },
    { title: "Dosage",      dataIndex: "dosage",                  key: "dosage",   width: 120 },
    { title: "Pharmacy",    dataIndex: "preferred_pharmacy_name", key: "pharmacy", render: n => n ?? <Text type="secondary">Any network pharmacy</Text> },
    { title: "Status",      dataIndex: "pickup_status",           key: "status",   width: 100,
      render: s => <Tag color={STATUS_COLOR[s] ?? "default"}>{s}</Tag> },
    { title: "Issued",      dataIndex: "created_at",              key: "date",     width: 120, render: fmtDate },
  ];

  const expandedRowRender = (record) => {
    const pin = record.dhn_pin;
    const rxs = patientRxMap[pin];
    const rxLoading = patientRxLoading[pin];
    if (rxLoading || rxs === undefined) return <Skeleton active paragraph={{ rows: 2 }} />;
    if (!rxs.length) return <Text type="secondary" style={{ padding: 8 }}>No pending prescriptions for this patient.</Text>;
    return (
      <Table
        columns={rxExpandCols}
        dataSource={rxs.map(r => ({ ...r, key: r.id }))}
        pagination={false}
        size="small"
        style={{ margin: "0 0 0 48px" }}
      />
    );
  };

  // ── Queue table ────────────────────────────────────────────────────────────
  const queueCols = [
    { title: "Patient",    dataIndex: "patient_display_name", key: "patient",   render: n => <Text strong>{n}</Text> },
    { title: "DHN PIN",    dataIndex: "patient_pin",          key: "pin",
      render: pin => <Text code style={{ fontWeight: 700, color: "#1677ff", fontSize: 15 }}>{pin ?? "—"}</Text> },
    { title: "Medication", dataIndex: "medication",           key: "med",        ellipsis: true },
    { title: "Pharmacy",   dataIndex: "preferred_pharmacy_name", key: "pharmacy",
      render: n => n ?? <Text type="secondary">Any network pharmacy</Text> },
    { title: "Status",     dataIndex: "pickup_status",        key: "status",     width: 100,
      render: s => <Tag color={STATUS_COLOR[s] ?? "default"}>{s}</Tag> },
    { title: "Issued",     dataIndex: "created_at",           key: "date",       width: 120, render: fmtDate },
  ];

  // ── Billing table ──────────────────────────────────────────────────────────
  const billingEvents = toArray(billingData?.events);
  const unbilledTotal = billingData?.unbilled_total ?? "0.00";

  const billingCols = [
    { title: "Date",        dataIndex: "created_at",     key: "date",    width: 130, render: fmtDate },
    { title: "Patient",     dataIndex: "patient_email",  key: "patient", ellipsis: true },
    { title: "Fee",         dataIndex: "fee_amount",     key: "fee",     width: 110, render: fmtCurrency },
    { title: "Invoice Ref", dataIndex: "invoice_ref",    key: "ref",     width: 150,
      render: r => r || <Text type="secondary">Pending</Text> },
  ];

  // ── Menu items ─────────────────────────────────────────────────────────────
  const menuItems = [
    { key: "patients",      icon: <TeamOutlined />,        label: "My Patients" },
    { key: "queue",         icon: <MedicineBoxOutlined />, label: "Pickup Queue" },
    { key: "billing",       icon: <DollarOutlined />,      label: "Billing" },
    { key: "notifications", icon: <BellOutlined />,        label: "Notifications" },
    { key: "settings",      icon: <SettingOutlined />,     label: "Settings" },
  ];

  // ── Stats cards for header ─────────────────────────────────────────────────
  const activePatients = patients.filter(p => p.is_active).length;
  const pendingTotal   = patients.reduce((sum, p) => sum + (p.pending_prescriptions ?? 0), 0);

  function renderContent() {
    switch (selectedMenu) {
      case "patients":
        return (
          <>
            <Title level={4} style={{ marginBottom: 16, color: "#262626" }}>My Authorised Patients</Title>
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
              <Col xs={24} sm={12} lg={6}>
                <Card variant="borderless" style={{ borderRadius: 8 }}>
                  <Statistic title={<Text style={{ color: "#8c8c8c", fontSize: 13 }}>Active Patients</Text>}
                    value={activePatients}
                    styles={{ content: { color: "#1677ff", fontSize: 28, fontWeight: 600 } }} />
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card variant="borderless" style={{ borderRadius: 8 }}>
                  <Statistic title={<Text style={{ color: "#8c8c8c", fontSize: 13 }}>Pending Pickups</Text>}
                    value={pendingTotal}
                    styles={{ content: { color: "#fa8c16", fontSize: 28, fontWeight: 600 } }} />
                </Card>
              </Col>
            </Row>
            <Card variant="borderless" style={{ borderRadius: 8 }}
              title={<Text strong>Authorised Patients</Text>}
              extra={<Button size="small" onClick={loadPatients}>Refresh</Button>}>
              <Table
                columns={patientCols}
                dataSource={patients}
                loading={loading}
                pagination={{ pageSize: 15, hideOnSinglePage: true }}
                locale={{ emptyText: "No patients have authorised your organisation yet." }}
                expandable={{
                  expandedRowRender,
                  onExpand: handleRowExpand,
                  expandedRowKeys: expandedPatient ? [patients.find(p => p.dhn_pin === expandedPatient)?.key].filter(Boolean) : [],
                }}
                size="small"
              />
            </Card>
          </>
        );

      case "queue":
        return (
          <>
            <Title level={4} style={{ marginBottom: 16, color: "#262626" }}>Pickup Queue</Title>
            <Card variant="borderless" style={{ borderRadius: 8 }}
              title={<Text strong>Prescriptions Pending Pickup</Text>}
              extra={<Button size="small" onClick={() => { setQueueLoaded(false); }}>Refresh</Button>}>
              <Table
                columns={queueCols}
                dataSource={queue}
                loading={queueLoading}
                pagination={{ pageSize: 20, hideOnSinglePage: true }}
                locale={{ emptyText: "No prescriptions pending pickup." }}
                size="small"
                rowClassName={(r) => r.pickup_status === "READY" ? "ant-table-row-green" : ""}
              />
            </Card>
          </>
        );

      case "billing":
        return (
          <>
            <Title level={4} style={{ marginBottom: 16, color: "#262626" }}>Billing</Title>
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
              <Col xs={24} sm={12} lg={6}>
                <Card variant="borderless" style={{ borderRadius: 8 }}>
                  <Statistic
                    title={<Text style={{ color: "#8c8c8c", fontSize: 13 }}>Fees Owed (unbilled)</Text>}
                    value={parseFloat(unbilledTotal).toFixed(2)}
                    prefix="₱"
                    styles={{ content: { color: "#f5222d", fontSize: 28, fontWeight: 600 } }}
                  />
                </Card>
              </Col>
            </Row>
            <Card variant="borderless" style={{ borderRadius: 8 }}
              title={<Text strong>Pickup Billing Events</Text>}
              extra={<Button size="small" onClick={() => { setBillingLoaded(false); }}>Refresh</Button>}>
              <Table
                columns={billingCols}
                dataSource={billingEvents.map(r => ({ ...r, key: r.id }))}
                loading={billingLoading}
                pagination={{ pageSize: 20, hideOnSinglePage: true }}
                locale={{ emptyText: "No billing events yet." }}
                size="small"
                summary={() => billingEvents.length > 0 && (
                  <Table.Summary.Row style={{ background: "#fafafa" }}>
                    <Table.Summary.Cell index={0} colSpan={2}><Text strong>Unbilled Total</Text></Table.Summary.Cell>
                    <Table.Summary.Cell index={2}><Text strong>{fmtCurrency(unbilledTotal)}</Text></Table.Summary.Cell>
                    <Table.Summary.Cell index={3} />
                  </Table.Summary.Row>
                )}
              />
            </Card>
          </>
        );

      case "notifications": {
        const NOTIF_TYPE_LABEL = {
          NEW_PICKUP_REQUEST:    "New Pickup Request",
          PICKUP_CANCELLED:      "Authorisation Removed",
          PRESCRIPTION_DISPENSED:"Dispensed",
        };
        const NOTIF_TYPE_COLOR = {
          NEW_PICKUP_REQUEST:    "purple",
          PICKUP_CANCELLED:      "orange",
          PRESCRIPTION_DISPENSED:"green",
        };
        const filtered = notifFilter === "unread"
          ? notifItems.filter(n => !n.is_read)
          : notifItems;

        const notifCols = [
          { title: "Type",    dataIndex: "notification_type", key: "type", width: 180,
            render: t => <Tag color={NOTIF_TYPE_COLOR[t] ?? "default"}>{NOTIF_TYPE_LABEL[t] ?? t}</Tag> },
          { title: "Title",   dataIndex: "title",   key: "title",   ellipsis: true,
            render: (t, r) => <Text strong={!r.is_read}>{t}</Text> },
          { title: "Message", dataIndex: "message", key: "message", ellipsis: true,
            render: m => <Text type="secondary" style={{ fontSize: 12 }}>{m}</Text> },
          { title: "Time",    dataIndex: "created_at", key: "time", width: 130,
            render: iso => iso ? new Date(iso).toLocaleString() : "—" },
          { title: "Status",  dataIndex: "is_read", key: "read", width: 90,
            render: v => v ? <Tag>Read</Tag> : <Tag color="blue">Unread</Tag> },
        ];

        return (
          <>
            <Title level={4} style={{ marginBottom: 16, color: "#262626" }}>Notifications</Title>
            <Card variant="borderless" style={{ borderRadius: 8 }}
              title={<Text strong>All Notifications</Text>}
              extra={
                <div style={{ display: "flex", gap: 8 }}>
                  <Button
                    size="small"
                    type={notifFilter === "unread" ? "primary" : "default"}
                    onClick={() => setNotifFilter(notifFilter === "unread" ? "all" : "unread")}>
                    {notifFilter === "unread" ? "Show All" : "Unread Only"}
                  </Button>
                  <Button size="small" onClick={() => { setNotifLoaded(false); }}>Refresh</Button>
                </div>
              }>
              <Table
                columns={notifCols}
                dataSource={filtered.map(r => ({ ...r, key: r.id }))}
                loading={notifLoading}
                pagination={{ pageSize: 20, hideOnSinglePage: true }}
                locale={{ emptyText: "No notifications yet." }}
                size="small"
                rowClassName={r => !r.is_read ? "ant-table-row-unread" : ""}
                onRow={r => ({
                  style: { background: r.is_read ? undefined : "#eff6ff" },
                })}
              />
            </Card>
          </>
        );
      }

      case "settings":
        return (
          <>
            <Title level={4} style={{ marginBottom: 24, color: "#262626" }}>Account Settings</Title>
            <ChangePasswordForm />
          </>
        );

      default: return null;
    }
  }

  return (
    <Layout style={{ minHeight: "100vh" }}>
      <Sider collapsible collapsed={collapsed} onCollapse={setCollapsed} width={220}
        style={{ background: "#fff", borderRight: "1px solid #f0f0f0" }}>
        <div style={{ height: 64, display: "flex", alignItems: "center", justifyContent: "center",
          borderBottom: "1px solid #f0f0f0", padding: "0 16px" }}>
          {!collapsed && (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", lineHeight: 1.3 }}>
              <Text strong style={{ color: "#1677ff", fontSize: 13, whiteSpace: "nowrap" }}>Digital Healthcare Network</Text>
              <Text style={{ color: "#1677ff", fontSize: 12, whiteSpace: "nowrap" }}>Partner Portal</Text>
            </div>
          )}
        </div>
        <Menu mode="inline" selectedKeys={[selectedMenu]} items={menuItems}
          onClick={({ key }) => setSelectedMenu(key)} style={{ border: "none", marginTop: 8 }} />
      </Sider>

      <Layout>
        <Header style={{ background: "#fff", padding: "0 24px", display: "flex", alignItems: "center",
          justifyContent: "space-between", borderBottom: "1px solid #f0f0f0", position: "sticky", top: 0, zIndex: 100 }}>
          <div>
            <Text strong style={{ fontSize: 16, color: "#1677ff" }}>MCHUSA</Text>
            <Text style={{ fontSize: 14, color: "#8c8c8c", marginLeft: 8 }}>| Partner Portal</Text>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <PartnerNotificationBell onNavigate={(section) => setSelectedMenu(section)} />
          <Dropdown menu={{ items: userMenuItems }} placement="bottomRight" arrow>
            <div style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
              <Avatar size={32} icon={<UserOutlined />} style={{ background: "#722ed1" }} />
              <Text style={{ fontSize: 14, color: "#262626" }}>{user?.email ?? "Partner"}</Text>
            </div>
          </Dropdown>
          </div>
        </Header>

        <Content style={{ padding: 24, background: "#f5f7fa" }}>
          {renderContent()}
        </Content>
      </Layout>
    </Layout>
  );
}
